import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import ExclamationTriangleSmallIcon from '@hapana/shared/web/styles/assets/svgs/exclamation-triangle-small.svg'
import AlertDialog from '@hapana/shared/web/components/shared/dialog/alert-dialog/alert-dialog.component'
import {
  SIZES,
  VARIANTS,
} from '@hapana/shared/web/components/shared/dialog/alert-dialog/alert-dialog.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { useCreateEquipmentAssignment } from '_hooks/equipment-assignments.hooks'

import AssignEquipmentSuccessAlert from './assign-equipment-success.alert.component'

export const AssignEquipmentErrorAlert = ({
  siteId,
  clientName,
  errorMessage,
  clientId,
  equipmentId,
  timerMinutes,
  delayMinutes,
}) => {
  const { t } = useTranslation('checkIn')
  const { popDialog, popToDialog, pushDialog } = useDialog()

  const { mutate: createAssignment, isLoading } = useCreateEquipmentAssignment({
    clientName,
  })

  const shouldShowOverride =
    typeof errorMessage === 'string' && /override required/i.test(errorMessage)
  const shouldStayInForm =
    typeof errorMessage === 'string' && !/unfinished assignment/i.test(errorMessage)
  const overrideNotAllowed =
    typeof errorMessage === 'string' && /override not allowed/i.test(errorMessage)

  const handleOverride = () => {
    createAssignment(
      {
        siteId,
        clientId,
        equipmentId,
        timerMinutes,
        delayMinutes,
        isOverride: true,
      },
      {
        onSuccess: () => {
          pushDialog(AssignEquipmentSuccessAlert, { clientName })
        },
        onError: (finalErr) => {
          pushDialog(AssignEquipmentErrorAlert, {
            siteId,
            clientName,
            errorMessage: finalErr.response?.errors?.[0]?.message,
            clientId,
            equipmentId,
            timerMinutes,
            delayMinutes,
          })
        },
      }
    )
  }

  return (
    <AlertDialog
      title={t('checkIn:equipments.alerts.assignToEquipment.errorTitle', { name: clientName })}
      description={
        errorMessage || t('checkIn:equipments.alerts.assignToEquipment.errorDescription')
      }
      icon={ExclamationTriangleSmallIcon}
      size={SIZES.XS}
      variant={VARIANTS.CRITICAL}
      closeAction={popDialog}
      primaryAction={{
        label: t('common:okay'),
        action: () => {
          if (shouldStayInForm && !overrideNotAllowed) {
            popDialog()
          } else {
            popToDialog((_, i) => i === 0, { defaultTab: '1' })
          }
        },
      }}
      secondaryAction={
        shouldShowOverride
          ? {
              label: t('checkIn:equipments.alerts.assignToEquipment.overrideAction'),
              action: handleOverride,
              props: {
                isLoading,
              },
            }
          : undefined
      }
    />
  )
}

AssignEquipmentErrorAlert.propTypes = {
  siteId: PropTypes.string,
  clientName: PropTypes.string.isRequired,
  errorMessage: PropTypes.string,
  clientId: PropTypes.string,
  equipmentId: PropTypes.string,
  timerMinutes: PropTypes.number,
  delayMinutes: PropTypes.number,
}

export default AssignEquipmentErrorAlert
